import { Component, OnInit, Input, HostBinding, ElementRef, EventEmitter, Output, HostListener } from '@angular/core';
import { ListKeyManagerOption, Highlightable } from '@angular/cdk/a11y';

/**
 * Needed to give every item a unique id
 */
let nextUniqueId = 0;

export type ItemSelectData = { value: any, external: boolean, triggeredByUser: boolean };

/**
 * An item (option) for dropdown menus. Wrap this component inside dropdown menus to define options.
 *
 * Usage:
 * ```html
 * <app-dropdown-item"
 *   [value]=""
 *   [disabled]=""
 * >
 * ```
 */
@Component({
  selector: 'app-dropdown-item',
  templateUrl: './dropdown-item.component.html',
  styleUrls: ['./dropdown-item.component.scss']
})
export class DropdownItemComponent implements OnInit, ListKeyManagerOption, Highlightable {
  private _isActive = false;

  @HostBinding()
  public id = `dropdown-item-${nextUniqueId++}`;

  @HostBinding('attr.role')
  private role = 'option';

  @HostBinding('class.active')
  public get isActive() {
    return this._isActive;
  }

  /**
   * Value/Object to identify the item (equivalent to value-attribute in a native select option)
   */
  @Input()
  public value: any;
  /**
   * True if item is disabled (not selectable)
   */
  @HostBinding('class.disabled')
  @HostBinding('attr.aria-disabled')
  @Input()
  public disabled = false;

  /** Emits the items' value and the event source if the item was selected */
  @Output()
  public itemSelect = new EventEmitter<ItemSelectData>();

  /**
   * True if the item is selected in the dropdown
   */
  @HostBinding('class.selected')
  @HostBinding('attr.aria-selected')
  public get selected() {
    return this._selected;
  }
  private _selected = false;

  /**
   * Returns the height of the HTML element (offsetHeight)
   */
  public get elementHeight(): number {
    return this.elem.nativeElement.offsetHeight;
  }

  /**
   * Returns the position of the HTML element with respect to the parent element (dropdown container, i.e. returns offsetTop)
   */
  public get elementOffsetTop(): number {
    return this.elem.nativeElement.offsetTop;
  }

  /** Returns the text inside the dropdown item */
  public get viewValue(): string {
    return this.elem.nativeElement.textContent.trim() ?? '';
  }

  constructor(private elem: ElementRef<HTMLElement>) { }

  public ngOnInit(): void {
  }

  /**
   * 
   * @param external Whether the selection was triggered from the item itself or from an external source
   */
  public select(external = true, triggeredByUser = true) {
    this._selected = true;
    this.itemSelect.emit({ value: this.value, external, triggeredByUser });
  }

  public deselect() {
    this._selected = false;
  }

  public getLabel(): any {
    return this.viewValue;
  }

  public setActiveStyles() {
    this._isActive = true;
  }

  public setInactiveStyles() {
    this._isActive = false;
  }

  @HostListener('click')
  public onClick() {
    this.select(false);
  }
}
